import { useState } from 'react';
import { useApi } from '@/hooks/useApi';
import {
  StatCard,
  Card,
  CardContent,
  PageHeader,
  Spinner,
} from '@theonefamily/ui';
import {
  Database,
  RefreshCw,
  CheckCircle2,
  XCircle,
  Clock,
  Link2,
  AlertTriangle,
  Activity,
  GitBranch,
} from 'lucide-react';

interface SyncStatus {
  last_synced_at: string | null;
  total_synced: number;
  failed_count: number;
  pending_count: number;
  cmdb_configured: boolean;
}

interface CmdbMapping {
  id: string;
  vehicle_id: string;
  vehicle_name: string;
  vehicle_number: string;
  vehicle_status: string;
  cmdb_asset_id: string;
  last_synced: string;
  status: 'synced' | 'failed' | 'pending';
  error?: string;
}

interface MappingsData {
  data: CmdbMapping[];
  total: number;
}

interface ImpactData {
  vehicle_id: string;
  vehicle_name: string;
  clients_served: {
    client_id: string;
    client_name: string;
    visit_count: number;
    dispatch_count: number;
    last_visit?: string;
  }[];
  total_clients: number;
  total_visits: number;
  total_dispatches: number;
  impact_summary: string;
}

const MOCK_STATUS: SyncStatus = {
  last_synced_at: '2026-03-10T03:00:00.000Z',
  total_synced: 41,
  failed_count: 2,
  pending_count: 0,
  cmdb_configured: true,
};

const MOCK_MAPPINGS: MappingsData = {
  data: [
    { id: '1', vehicle_id: 'v1', vehicle_name: 'Unit 101 - Ford F-150', vehicle_number: '101', vehicle_status: 'active', cmdb_asset_id: 'CI-00821', last_synced: '2026-03-10T03:00:00.000Z', status: 'synced' },
    { id: '2', vehicle_id: 'v2', vehicle_name: 'Unit 102 - Chevy Silverado', vehicle_number: '102', vehicle_status: 'active', cmdb_asset_id: 'CI-00822', last_synced: '2026-03-10T03:00:00.000Z', status: 'synced' },
    { id: '3', vehicle_id: 'v3', vehicle_name: 'Unit 103 - Ram 1500', vehicle_number: '103', vehicle_status: 'maintenance', cmdb_asset_id: '', last_synced: '2026-03-09T03:00:00.000Z', status: 'failed', error: 'CMDB returned 422: Asset serial already exists' },
    { id: '4', vehicle_id: 'v4', vehicle_name: 'Unit 104 - Toyota Tacoma', vehicle_number: '104', vehicle_status: 'active', cmdb_asset_id: 'CI-00824', last_synced: '2026-03-10T03:00:00.000Z', status: 'synced' },
    { id: '5', vehicle_id: 'v5', vehicle_name: 'Unit 105 - Ford Transit', vehicle_number: '105', vehicle_status: 'active', cmdb_asset_id: 'CI-00825', last_synced: '2026-03-10T03:00:00.000Z', status: 'synced' },
  ],
  total: 5,
};

const STATUS_STYLES = {
  synced: { bg: 'rgba(16,185,129,0.15)', text: '#10B981', label: 'Synced' },
  failed: { bg: 'rgba(239,68,68,0.15)', text: '#EF4444', label: 'Failed' },
  pending: { bg: 'rgba(245,158,11,0.15)', text: '#F59E0B', label: 'Pending' },
};

function StatusBadge({ status }: { status: 'synced' | 'failed' | 'pending' }) {
  const s = STATUS_STYLES[status] ?? { bg: 'rgba(156,163,175,0.15)', text: '#9CA3AF', label: status };
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium"
      style={{ background: s.bg, color: s.text }}
    >
      {status === 'synced' && <CheckCircle2 className="w-3 h-3" />}
      {status === 'failed' && <XCircle className="w-3 h-3" />}
      {status === 'pending' && <Clock className="w-3 h-3" />}
      {s.label}
    </span>
  );
}

export default function CmdbSyncPage() {
  const { data: statusData, isLoading: statusLoading, refetch: refetchStatus } = useApi<SyncStatus>('/api/cmdb-sync/status');
  const { data: mappingsData, isLoading: mappingsLoading, refetch: refetchMappings } = useApi<MappingsData>('/api/cmdb-sync/mappings');

  const status = statusData ?? MOCK_STATUS;
  const mappings = mappingsData ?? MOCK_MAPPINGS;

  const [isSyncing, setIsSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<{ synced: number; failed: number } | null>(null);
  const [selectedVehicle, setSelectedVehicle] = useState<string | null>(null);
  const [impactData, setImpactData] = useState<ImpactData | null>(null);
  const [impactLoading, setImpactLoading] = useState(false);

  const handleSyncAll = async () => {
    setIsSyncing(true);
    setSyncResult(null);
    try {
      const resp = await fetch('/api/cmdb-sync/push', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      if (resp.ok) {
        const json = await resp.json() as { synced: number; failed: number };
        setSyncResult(json);
        refetchStatus?.();
        refetchMappings?.();
      }
    } catch { /* ignore */ }
    setIsSyncing(false);
  };

  const handleImpactAnalysis = async (vehicleId: string) => {
    if (selectedVehicle === vehicleId && impactData) {
      setSelectedVehicle(null);
      setImpactData(null);
      return;
    }
    setSelectedVehicle(vehicleId);
    setImpactLoading(true);
    setImpactData(null);
    try {
      const resp = await fetch(`/api/cmdb-sync/impact/${vehicleId}`, {
        method: 'POST',
        credentials: 'include',
      });
      if (resp.ok) {
        const json = await resp.json() as ImpactData;
        setImpactData(json);
      }
    } catch { /* ignore */ }
    setImpactLoading(false);
  };

  const lastSyncedFormatted = status.last_synced_at
    ? new Date(status.last_synced_at).toLocaleString()
    : 'Never';

  return (
    <div className="space-y-6">
      <PageHeader
        title="CMDB Asset Sync"
        description="Push fleet vehicles to The One CMDB as configuration items"
        actions={
          <div className="flex items-center gap-3">
            {(statusLoading || mappingsLoading) && <Spinner size={16} />}
            <button
              type="button"
              onClick={handleSyncAll}
              disabled={isSyncing}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all"
              style={{
                background: isSyncing ? 'rgba(16,185,129,0.15)' : 'rgba(16,185,129,0.2)',
                color: '#10B981',
                border: '1px solid rgba(16,185,129,0.3)',
              }}
            >
              <RefreshCw className={`w-4 h-4 ${isSyncing ? 'animate-spin' : ''}`} />
              {isSyncing ? 'Syncing...' : 'Sync All Vehicles'}
            </button>
          </div>
        }
      />

      {/* Sync Result Banner */}
      {syncResult && (
        <div
          className="flex items-center gap-3 p-4 rounded-xl"
          style={{
            background: syncResult.failed > 0 ? 'rgba(245,158,11,0.1)' : 'rgba(16,185,129,0.1)',
            border: `1px solid ${syncResult.failed > 0 ? 'rgba(245,158,11,0.3)' : 'rgba(16,185,129,0.3)'}`,
          }}
        >
          {syncResult.failed > 0 ? (
            <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0" />
          ) : (
            <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0" />
          )}
          <span className="text-sm" style={{ color: 'var(--foreground)' }}>
            Sync complete: <strong>{syncResult.synced}</strong> vehicles synced
            {syncResult.failed > 0 && <>, <strong className="text-red-400">{syncResult.failed}</strong> failed</>}
          </span>
          <button
            type="button"
            onClick={() => setSyncResult(null)}
            className="ml-auto text-xs"
            style={{ color: 'var(--muted-foreground)' }}
          >
            Dismiss
          </button>
        </div>
      )}

      {/* CMDB Config Warning */}
      {!status.cmdb_configured && (
        <div
          className="flex items-center gap-3 p-4 rounded-xl"
          style={{ background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.3)' }}
        >
          <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0" />
          <span className="text-sm text-amber-300">
            CMDB not configured — set <code className="text-xs bg-amber-500/20 px-1 rounded">CMDB_API_URL</code> and{' '}
            <code className="text-xs bg-amber-500/20 px-1 rounded">CMDB_INTEGRATION_KEY</code> on the API function app.
            Sync records will be stored locally.
          </span>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={CheckCircle2} label="Synced Vehicles" value={status.total_synced} accent="success" />
        <StatCard icon={Clock} label="Last Sync" value={lastSyncedFormatted} />
        <StatCard icon={Activity} label="Pending" value={status.pending_count} />
        <StatCard icon={XCircle} label="Failed" value={status.failed_count} accent={status.failed_count > 0 ? 'destructive' : undefined} />
      </div>

      {/* Mapping Table */}
      <Card>
        <CardContent className="p-0">
          <div className="flex items-center justify-between px-5 py-4" style={{ borderBottom: '1px solid var(--glass-border)' }}>
            <div className="flex items-center gap-2">
              <Link2 className="w-4 h-4" style={{ color: 'var(--accent)' }} />
              <h3 className="text-sm font-semibold" style={{ color: 'var(--foreground)' }}>
                Vehicle → CMDB Mappings
              </h3>
            </div>
            <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
              {mappings.total} vehicles
            </span>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr style={{ borderBottom: '1px solid var(--glass-border)' }}>
                  {['Vehicle', 'CMDB Asset ID', 'Last Synced', 'Status', 'Actions'].map(col => (
                    <th
                      key={col}
                      className="text-left px-5 py-3 text-xs font-medium uppercase tracking-wide"
                      style={{ color: 'var(--muted-foreground)' }}
                    >
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {mappings.data.map(m => (
                  <>
                    <tr
                      key={m.id}
                      className="transition-colors"
                      style={{ borderBottom: '1px solid var(--glass-border)' }}
                      onMouseEnter={e => (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.02)'}
                      onMouseLeave={e => (e.currentTarget as HTMLElement).style.background = 'transparent'}
                    >
                      <td className="px-5 py-3">
                        <div className="font-medium" style={{ color: 'var(--foreground)' }}>{m.vehicle_name}</div>
                        <div className="text-xs mt-0.5" style={{ color: 'var(--muted-foreground)' }}>#{m.vehicle_number}</div>
                      </td>
                      <td className="px-5 py-3">
                        {m.cmdb_asset_id ? (
                          <span className="font-mono text-xs" style={{ color: 'var(--foreground)' }}>{m.cmdb_asset_id}</span>
                        ) : (
                          <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>—</span>
                        )}
                      </td>
                      <td className="px-5 py-3">
                        <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
                          {new Date(m.last_synced).toLocaleString()}
                        </span>
                      </td>
                      <td className="px-5 py-3">
                        <div className="space-y-1">
                          <StatusBadge status={m.status} />
                          {m.error && (
                            <div className="text-xs text-red-400 max-w-xs truncate" title={m.error}>
                              {m.error}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-5 py-3">
                        <button
                          type="button"
                          onClick={() => handleImpactAnalysis(m.vehicle_id)}
                          className="flex items-center gap-1 text-xs px-2 py-1 rounded transition-colors"
                          style={{
                            background: selectedVehicle === m.vehicle_id ? 'rgba(99,102,241,0.2)' : 'rgba(99,102,241,0.1)',
                            color: '#818CF8',
                            border: '1px solid rgba(99,102,241,0.3)',
                          }}
                        >
                          <GitBranch className="w-3 h-3" />
                          Impact
                        </button>
                      </td>
                    </tr>

                    {/* Impact Analysis Panel */}
                    {selectedVehicle === m.vehicle_id && (
                      <tr key={`${m.id}-impact`} style={{ background: 'rgba(99,102,241,0.05)' }}>
                        <td colSpan={5} className="px-5 py-4">
                          {impactLoading ? (
                            <div className="flex items-center gap-2 text-sm" style={{ color: 'var(--muted-foreground)' }}>
                              <Spinner size={14} />
                              Loading impact analysis...
                            </div>
                          ) : impactData ? (
                            <div className="space-y-3">
                              <p className="text-sm" style={{ color: 'var(--foreground)' }}>{impactData.impact_summary}</p>
                              {impactData.clients_served.length > 0 && (
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                                  {impactData.clients_served.slice(0, 6).map(client => (
                                    <div
                                      key={client.client_id}
                                      className="flex items-center justify-between rounded-lg px-3 py-2"
                                      style={{
                                        background: 'var(--glass-bg)',
                                        border: '1px solid var(--glass-border)',
                                      }}
                                    >
                                      <span className="text-xs font-medium truncate" style={{ color: 'var(--foreground)' }}>
                                        {client.client_name}
                                      </span>
                                      <span className="text-xs ml-2 flex-shrink-0" style={{ color: 'var(--muted-foreground)' }}>
                                        {client.visit_count} visits
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              )}
                              {impactData.clients_served.length === 0 && (
                                <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>No client visits recorded for this vehicle.</p>
                              )}
                            </div>
                          ) : null}
                        </td>
                      </tr>
                    )}
                  </>
                ))}
                {mappings.data.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-5 py-8 text-center text-sm" style={{ color: 'var(--muted-foreground)' }}>
                      No sync mappings yet — click "Sync All Vehicles" to push vehicles to CMDB.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Integration Info */}
      <div
        className="flex items-start gap-3 p-4 rounded-xl"
        style={{
          background: 'var(--glass-bg)',
          border: '1px solid var(--glass-border)',
          backdropFilter: 'blur(var(--glass-blur))',
        }}
      >
        <Database className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: 'var(--accent)' }} />
        <div className="space-y-1">
          <p className="text-sm font-medium" style={{ color: 'var(--foreground)' }}>CMDB Integration</p>
          <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
            Each vehicle is mapped to a CMDB configuration item with asset type "vehicle". Custom fields include mileage, fuel type, assigned driver, compliance dates, and GPS device ID.
            Impact analysis cross-references visit logs and dispatches to show which clients each vehicle serves.
          </p>
        </div>
      </div>
    </div>
  );
}
